import { getStripePublicKeyVersion } from 'client/api/subscriptionManagement'
import * as globalWindow from 'client/globalWindow'
import { VatNumberValidation } from 'client/Interfaces/CompanyInterface'
import moment from 'moment'

import { Config } from '../../config'
import {
  CouponStateInterface,
  PaymentMethodInterface,
  QuotaTypes,
  SubscriptionInterface,
  SubscriptionPlanInterface,
} from '../../Interfaces/Subscription'
import subscriptionKeys from '../txtkeys/subscriptionKeys'

const rounding = (price: number) => (price % 1 !== 0 ? price.toFixed(2) : price.toFixed(0))
export const currencyLabel = (currency: string) => {
  if (!currency) {
    return '€'
  }
  switch (currency.toLocaleLowerCase()) {
    case 'eur':
      return '€'
    case 'gbp':
      return '£'
    case 'sek':
      return 'kr'
    default:
      return currency
  }
}

const EUROPEAN_COUNTRIES = [
  'AT',
  'AX',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  'MC',
  'IM',
]

export function isCountryInEurope(code: string) {
  return EUROPEAN_COUNTRIES.includes(code?.toUpperCase())
}

export function stripeErrorCodeTranslations(message: string) {
  const strings: {
    [key: string]: string
  } = {
    'card has insufficient': subscriptionKeys.stripeErrors.insufficient_funds,
    'card has expired': subscriptionKeys.stripeErrors.expired_card,
    'security code is incorrect': subscriptionKeys.stripeErrors.incorrect_cvc,
  }
  for (const key in strings) {
    if (message.includes(key)) {
      return strings[key]
    }
  }
  return subscriptionKeys.stripeErrors.generic_error
}

export function isVATForced(code: string) {
  return ['FI', 'AX'].includes(code?.toUpperCase())
}

export function noSubscriptionKeys(subscription: SubscriptionInterface, group: string) {
  const titleKeys = {
    no_subscription_with_referral: `subscription.${group}.no_subscription_with_referral.title`,
    no_subscription_without_referral: `subscription.${group}.no_subscription_without_referral.title`,
    free: `subscription.${group}.no_subscription.free.title`,
  }
  const descriptionKeys = {
    no_subscription_with_referral: `subscription.${group}.no_subscription_with_referral.description`,
    no_subscription_without_referral: `subscription.${group}.no_subscription_without_referral.description`,
    free: `subscription.${group}.no_subscription.free.description`,
  }
  let titleKey
  let descKey
  if (subscription.status === 'no_subscription') {
    if (subscription.referralIdUsed) {
      titleKey = titleKeys.no_subscription_with_referral
      descKey = descriptionKeys.no_subscription_with_referral
    } else {
      titleKey = titleKeys.no_subscription_without_referral
      descKey = descriptionKeys.no_subscription_without_referral
    }
  } else {
    titleKey = titleKeys[subscription.status] || titleKeys.no_subscription_without_referral
    descKey = descriptionKeys[subscription.status] || descriptionKeys.no_subscription_without_referral
  }
  return { titleKey, descKey }
}

export const priceAndCurrency = (priceInCents: number, currency: string) =>
  currency === 'gbp'
    ? `${currencyLabel(currency)} ${rounding(priceInCents / 100)}`
    : `${rounding(priceInCents / 100)} ${currencyLabel(currency)}`

export const shownQuotaAmount = (name: QuotaTypes, plan: SubscriptionPlanInterface): number => {
  const quota = plan.quotas.find((item) => item.name === name)
  if (!quota) {
    return
  }
  if (!plan.id.includes('ageras') && plan.billingCycle === 'year' && name === QuotaTypes.INVOICE && quota.quota) {
    return quota ? quota.quota / 12 : quota.quota
  }
  return quota.quota
}

export const vatAmount = (price: number, vatRate: number) => price * vatRate

export const duePrice = (
  subscriptionPlan: SubscriptionPlanInterface,
  vatLiable: boolean,
  currentPrice = 0,
  vatRate: number,
  coupon?: CouponStateInterface,
) => {
  let price = subscriptionPlan.price

  price = priceWithCoupon(price, coupon, subscriptionPlan.currency)

  const result = vatLiable ? price + vatAmount(price - currentPrice, vatRate) - currentPrice : price - currentPrice
  return result
}

export const premiumInvoices = (plan: SubscriptionPlanInterface) => {
  const premiumInvoices = plan.quotas.find((quota) => quota.name === QuotaTypes.INVOICE)
  return (premiumInvoices && premiumInvoices.quota) || 0
}

export const applicableToFreeTrial = (
  plan: SubscriptionPlanInterface,
  currentSubscription: SubscriptionInterface,
): boolean => {
  const freeTrialStatuses: Array<SubscriptionInterface['status']> = ['trialing', 'no_subscription']
  return Boolean(freeTrialStatuses.includes(currentSubscription.status) && plan.freeTrialDays && plan.freeTrialDays > 0)
}

export const isVatLiable = (
  customerCountry: string,
  vatNumber: string,
  vatNumberValidation: VatNumberValidation,
  isNotVATLiable = false,
): boolean => {
  if (!customerCountry) {
    return true
  }
  const euCountry = isCountryInEurope(customerCountry)
  if (!euCountry) {
    return false
  }
  const vatInvalid = !vatNumber || !['VALID', 'SKIP'].includes(vatNumberValidation)
  if (isVATForced(customerCountry) || (vatInvalid && !isNotVATLiable)) {
    return true
  }

  return false
}

export function isPaidPlan(subscription: SubscriptionInterface) {
  return subscription && subscription.plan && !['no_subscription', 'free'].includes(subscription.status)
}

export const getIsPlanCancelled = (subscription: SubscriptionInterface): boolean => {
  if (!subscription) {
    return false
  }
  return subscription.inDowngrade && subscription.downgradeTo.endsWith('_free')
}

export function allowSendingReminders(subscription: SubscriptionInterface): boolean {
  return (
    subscription &&
    subscription.plan &&
    !!subscription.plan.quotas.find((q) => q.name === QuotaTypes.REMINDERS && q.quota > 0)
  )
}

export const isCouponValid = (coupon: CouponStateInterface, currency: string): boolean => {
  return (
    coupon?.loaded &&
    Boolean(coupon?.value) &&
    coupon?.value.valid === true &&
    (coupon?.value.currency === currency || coupon.value.percent_off !== null)
  )
}

export const priceWithCoupon = (price: number, coupon: CouponStateInterface, currency): number => {
  if (isCouponValid(coupon, currency)) {
    const { amount_off: amountOff, percent_off: percentOff } = coupon.value
    // eslint-disable-next-line no-nested-ternary
    price = amountOff ? (amountOff > price ? 0 : price - amountOff) : price - price * (percentOff / 100)
  }

  return price
}

export const daysUntil = (day: number) => Math.round((day - Date.now()) / (1000 * 60 * 60 * 24))

export const authorizePayment = async (subscription: SubscriptionInterface): Promise<boolean> => {
  const version = await getStripePublicKeyVersion()
  const stripePublicKey = version === 'v2' ? Config.getStripeApiKeyV2() : Config.getStripeApiKey()
  try {
    // Lets create our own stripe instance,
    // because in some cases the stripe created with react-stripe-elements is polluted
    const stripe = globalWindow.Stripe(stripePublicKey)
    if (subscription.payment.intent) {
      if (subscription.payment.sepa) {
        const result =
          subscription.payment.payment_status === 'requires_setup_intent'
            ? await stripe.confirmSepaDebitSetup(subscription.payment.intent.client_secret, {
                payment_method: subscription.payment.intent.paymentMethodId,
              })
            : await stripe.confirmSepaDebitPayment(subscription.payment.intent.client_secret, {
                payment_method: subscription.payment.intent.paymentMethodId,
              })
        if (result.error) {
          console.error('Authorizing payment failed with ', result.error)
          return false
        }
      } else {
        const result =
          subscription.payment.payment_status === 'requires_setup_intent'
            ? await stripe.confirmCardSetup(subscription.payment.intent.client_secret, {
                payment_method: subscription.payment.intent.paymentMethodId,
              })
            : await stripe.confirmCardPayment(subscription.payment.intent.client_secret, {
                payment_method: subscription.payment.intent.paymentMethodId,
              })
        if (result.error) {
          console.error('Authorizing payment failed with ', result.error)
          return false
        }
      }
    }
    return true
  } catch (err) {
    console.error('Authorizing payment failed with ', err)
    return false
  }
}

export const isPastDuePayment = (payment: PaymentMethodInterface) =>
  Boolean(payment?.dueInvoices) && payment.dueInvoices.some((due) => due.entityType !== 'ConsumablesEntity')
export const isDisputedPayment = (payment: PaymentMethodInterface) => Boolean(payment?.disputesInfo)

export const isPaymentCardExpiring = (subscription: SubscriptionInterface) => {
  const { notificationNeeded } = hasCardExpired(subscription)
  return notificationNeeded
}

export const isSEPADisputedPayment = (payment: PaymentMethodInterface) =>
  isDisputedPayment(payment) &&
  payment.disputesInfo.some((dispute) => dispute.entityType !== 'ConsumablesEntity' && dispute.SEPALast4)

export const isCardDisputePayment = (payment: PaymentMethodInterface) =>
  isDisputedPayment(payment) &&
  payment.disputesInfo.some((dispute) => dispute.entityType !== 'ConsumablesEntity' && dispute.cardLast4)

export const isSEPADispute = (payment: PaymentMethodInterface) =>
  isDisputedPayment(payment) && Boolean(payment.disputesInfo[0]?.SEPALast4)

export const isConsumablesPastDuePayment = (payment: PaymentMethodInterface) =>
  Boolean(payment?.dueInvoices) && Boolean(payment.dueInvoices[0]?.entityType === 'ConsumablesEntity')

export const isConsumablesDisputedPayment = (payment: PaymentMethodInterface) =>
  isDisputedPayment(payment) &&
  Boolean(payment.disputesInfo && payment.disputesInfo[0]?.entityType === 'ConsumablesEntity')

export const keysForPaymentCard = {
  cardExpiringIn30DaysFirstHalf: 'payment-card.expiring.firstHalf',
  cardExpiringIn30DaysSecondHalf: 'payment-card.expiring.secondHalf',
  cardExpiredFirstHalf: 'payment-card.expired.firstHalf',
  cardExpiredSecondHalf: 'payment-card.expired.secondHalf',
  cardExpiringMessage: 'payment-card.expiring.message',
  updatePaymentCard: 'payment-card.update',
}

export interface PaymentCardState {
  cardExpiringIn30Days: boolean
  cardExpiringIn2Weeks: boolean
  cardExpired: boolean
  notificationNeeded: boolean
  expiryDate: string
  last4: string
}

export const hasCardExpired = (
  subscription: SubscriptionInterface,
  currentDate: Date = new Date(),
): PaymentCardState => {
  const card = subscription?.payment?.card
  if (!card) {
    return {
      cardExpiringIn30Days: false,
      cardExpiringIn2Weeks: false,
      cardExpired: false,
      notificationNeeded: false,
      expiryDate: '',
      last4: '',
    }
  }
  const { last4 } = card
  const [expiryMonth, expiryYear] = card.expiry?.split('/').map((str) => Number(str))
  const lastDayOfExpiryMonth = new Date(expiryYear, expiryMonth, 0, 23, 59, 59).getDate()
  const expiry = new Date(expiryYear, expiryMonth - 1, lastDayOfExpiryMonth)

  const oneMonthBeforeExpiry = new Date(expiry)
  oneMonthBeforeExpiry.setMonth(expiry.getMonth() - 1)

  const twoWeeksBeforeExpiry = new Date(expiry)
  twoWeeksBeforeExpiry.setDate(expiry.getDate() - 14)

  const cardExpiringIn30Days = oneMonthBeforeExpiry <= currentDate && currentDate < expiry
  const cardExpiringIn2Weeks = twoWeeksBeforeExpiry <= currentDate && currentDate < expiry
  const cardExpired = expiry < currentDate

  return {
    cardExpiringIn30Days,
    cardExpiringIn2Weeks,
    cardExpired,
    notificationNeeded: cardExpiringIn30Days || cardExpiringIn2Weeks || cardExpired,
    expiryDate: `1.${expiryMonth + 1}.${expiryYear}`,
    last4,
  }
}

export const hasSubscriptionBeenCancelled = (subscription: SubscriptionInterface): boolean => {
  if (subscription?.inDowngrade || (subscription.plan.name === 'plan.free' && subscription.payment.card)) {
    return true
  }
  return false
}

export const hasFailedPayment = (subscription: SubscriptionInterface) => {
  return (
    subscription &&
    subscription.payment &&
    (Boolean(subscription.payment.disputesInfo) || Boolean(subscription.payment.dueInvoices))
  )
}

export const failedPaymentModalInfo = (daysPastDue?: number) => {
  let dueTranslationKey = ''
  if (daysPastDue && daysPastDue > 730) {
    dueTranslationKey = 'NotBlocked'
  }
  if (daysPastDue && daysPastDue < 14) {
    dueTranslationKey = 'BeforeBlocked'
  }

  const blockAccountAccess = daysPastDue && daysPastDue > 14 && daysPastDue < 730
  return {
    blockAccountAccess,
    dueTranslationKey,
    daysPastDue,
  }
}
